import { Combobox } from '@vwfs-bronson/bronson-react';
import { useField, useFormikContext } from 'formik';
import { t } from 'i18next';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { FormField } from '..';
import { FormFieldPropsBase } from './fieldset.model';

interface SearchableSelectFieldProps extends FormFieldPropsBase {
  optionList: Array<{ key: string; value: string }>;
  defaultValue?: string;
  searchable?: boolean;
}

interface CustomOption {
  textValue: string;
  content: React.ReactNode;
}

interface ComboboxOption {
  key: string;
  value: string | CustomOption;
  disabled?: boolean;
}

export const SearchableSelectFormField: FunctionComponent<SearchableSelectFieldProps> = ({
  optionList,
  labelTranslationKey = '',
  placeholderTranslationKey = '',
  isDisabled,
  fieldName,
  testId,
  optional,
  searchable = true,
}): React.JSX.Element => {
  const { setFieldTouched, setFieldValue } = useFormikContext<any>();
  const [field] = useField(fieldName);
  const [internalFieldValue, setInternalFieldValue] = useState<string | undefined>(undefined);

  const preselectedValue = optionList.find((o) => o.key === field.value);

  const handleChange = (value: ComboboxOption[]) => {
    if (value instanceof Array && value.length) {
      setInternalFieldValue(value[0].key);
    }
  };

  const handleBlur = () => {
    setFieldTouched(fieldName, true, true);
  };

  useEffect(() => {
    setFieldValue(fieldName, internalFieldValue);
  }, [internalFieldValue, setFieldValue, fieldName]);

  useEffect(() => {
    if (preselectedValue) {
      setInternalFieldValue(preselectedValue.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormField
      type="select"
      name={fieldName}
      testId={testId ?? fieldName}
      labelText={`${t(labelTranslationKey)}${optional ? t('formControls:optionalLabelAffix') : ''}`}
      isDisabled={isDisabled}
      render={() => (
        <Combobox
          id={fieldName}
          disabled={isDisabled}
          testId={testId}
          aria-disabled={isDisabled}
          noOptionsMessage={t('formControls:combobox.optionNotFound')}
          defaultState={
            preselectedValue ? { value: [{ key: preselectedValue.key, value: preselectedValue.value }] } : undefined
          }
          placeholder={t(placeholderTranslationKey)}
          searchable={searchable}
          options={optionList}
          onChange={handleChange}
          onBlur={handleBlur}
          className="u-text-left"
        >
          {optionList.map((o) => (
            <Combobox.Item optionKey={o.key} optionValue={o.value} key={o.key}>
              {o.value}
            </Combobox.Item>
          ))}
        </Combobox>
      )}
    />
  );
};
